import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Topbar from '../../PulseSurvey/Topbar';
import Loader from '../../../component/common/Loader';
import ActionCard from '../../../component/B2BDashboard/ActionCard';
import { PartnerApi } from '../../../api/partnerApi';
import { getLocalStorageItem } from '../../../utils/helper';
import {
  StatCard,
  MiniDonutCard,
  STAT_ICONS,
  toneColors
} from '../../../component/common/designSystem';

/**
 * The Partner panel's landing page, on the design system.
 *
 * The data is unchanged — GET /partner-content-counts, five active/inactive
 * pairs. What changed is how it reads: the old page drew five donuts in ten
 * hand-picked hexes, so "active" was dark blue on one card, pink on the next
 * and mustard on the fifth. Active and inactive now carry the same two tones
 * on every card, and the totals a partner actually opens this page for sit in
 * a stat row above the breakdowns rather than inside five donut holes.
 */

// One meaning, one colour, on every card. See theme.js — never a hex here.
const SPLIT_COLORS = toneColors(['success', 'neutral']);

const PAIRS = [
  {
    key: 'companies',
    title: 'All companies',
    active: 'activeCompanies',
    inactive: 'inactiveCompanies',
    unit: 'companies'
  },
  {
    key: 'negotiation',
    title: 'In negotiation',
    active: 'negotiationActive',
    inactive: 'negotiationInactive',
    unit: 'companies'
  },
  {
    key: 'signed',
    title: 'Signed',
    active: 'signedActive',
    inactive: 'signedInactive',
    unit: 'companies'
  },
  {
    key: 'users',
    title: 'B2B users',
    active: 'activeCompaniesUsers',
    inactive: 'inactiveCompaniesUsers',
    unit: 'users'
  },
  {
    key: 'seats',
    title: 'Seats under contract',
    active: 'totalactiveSeats',
    inactive: 'totalinactiveSeats',
    unit: 'seats'
  }
];

const num = (value) => Number(value) || 0;

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(null);

  const userData = useMemo(() => {
    const raw = getLocalStorageItem('userData');
    return raw ? JSON.parse(raw) : {};
  }, []);

  useEffect(() => {
    let cancelled = false;

    PartnerApi.getPartnerContentCounts()
      .then((res) => {
        if (!cancelled && res?.data?.meta?.code === 1) setCounts(res.data.data || {});
      })
      .catch((error) => console.error('Partner counts failed to load', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const total = (pair) => num(counts?.[pair.active]) + num(counts?.[pair.inactive]);
  const figure = (value) => (loading ? '…' : value.toLocaleString());

  return (
    <div className="relative lg:px-5 lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Dashboard | Shoorah Partner</title>
      </Helmet>
      {loading ? <Loader /> : null}

      <Topbar title={userData?.name ? `Hi ${userData.name}` : 'Dashboard'} />

      <div className="lg:mt-4 mb-4">
        <h1 className="text-lg md:text-xl font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
          Your partnership
        </h1>
        <p className="text-xs text-shoorah-gray3">
          The companies you have introduced, and how far each one has come
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard
          icon={STAT_ICONS.company}
          title="Companies introduced"
          value={figure(total(PAIRS[0]))}
        />
        <StatCard icon={STAT_ICONS.completions} title="Signed" value={figure(total(PAIRS[2]))} />
        <StatCard icon={STAT_ICONS.users} title="B2B users" value={figure(total(PAIRS[3]))} />
        <StatCard
          icon={STAT_ICONS.seats}
          title="Seats under contract"
          value={figure(total(PAIRS[4]))}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-3">
        <ActionCard
          icon={STAT_ICONS.newUsers}
          title="Introduce a company"
          label="Start"
          onClick={() => navigate('/introduce/add-company')}
        />
        <ActionCard
          icon={STAT_ICONS.catalog}
          title="Companies introduced"
          label="See all"
          onClick={() => navigate('/introduce')}
        />
        <ActionCard
          icon={STAT_ICONS.revenue}
          title="Earnings"
          label="View"
          onClick={() => navigate('/partner-earning')}
        />
        <ActionCard
          icon={STAT_ICONS.partner}
          title="Brand assets"
          label="Download"
          onClick={() => navigate('/partner-assets')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mt-6">
        {PAIRS.map((pair) => (
          <MiniDonutCard
            key={pair.key}
            title={pair.title}
            subtitle="Active and inactive"
            unit={pair.unit}
            colors={SPLIT_COLORS}
            legendLimit={2}
            data={[
              { label: 'Active', count: num(counts?.[pair.active]) },
              { label: 'Inactive', count: num(counts?.[pair.inactive]) }
            ]}
          />
        ))}
      </div>
    </div>
  );
};

export default PartnerDashboard;
